import type { ReactNode } from 'react';
import { LatexText } from '../latex';
import './MCQOptionBadge.css';

interface MCQOptionBadgeProps {
  /** The full option object (label + text + optional image). */
  option: { label: string; text: string; imageUrl?: string | null };
  /**
   * Visual variant:
   *  - 'correct'   — green border + label (student's correct answer)
   *  - 'incorrect' — red border + label (student's wrong answer)
   *  - 'neutral'   — default grey, no highlight (correct answer display)
   */
  variant?: 'correct' | 'incorrect' | 'neutral';
  /** Rendered at the right edge INSIDE the card. For a verdict on the option
   *  itself (a "Not correct" pill), which belongs in the same box as the answer
   *  it judges rather than floating beside it. */
  trailing?: ReactNode;
}

/**
 * Read-only display of a single MCQ option, matching the interactive
 * MCQOptionCard styling from the question panel.
 *
 * Reusable across review panes, answer snapshots, correct answer displays, etc.
 */
export function MCQOptionBadge({ option, variant = 'neutral', trailing }: MCQOptionBadgeProps) {
  return (
    <div className={`mcq-option-card disabled mcq-badge--${variant}`}>
      <div className={`mcq-option-label mcq-badge-label--${variant}`}>
        {option.label.toUpperCase()}
      </div>
      <div className="mcq-option-content">
        <span className="mcq-option-text"><LatexText text={option.text} /></span>
        {option.imageUrl && (
          <img src={option.imageUrl} alt={`Option ${option.label}`} className="mcq-option-image" />
        )}
      </div>
      {trailing && <div className="mcq-option-trailing">{trailing}</div>}
    </div>
  );
}
